import { CommonModule } from '@angular/common';
import { DoBootstrap, EnvironmentProviders, Injector, NgModule, Provider, importProvidersFrom, inject } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { CdsInputsModule } from '@celerofinancas/design-system';
import { UiDirectivesModule } from '@celerofinancas/ui-directives';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { environment } from '@environments/environment';
import { provideNgxMask } from 'ngx-mask';
import { routes } from './app.routes';

/**
 * Name the elements build gives the bundle's stylesheet. Fixed by the builder, which
 * derives it from the single entry in `styles` (`src/styles.scss`).
 */
const STYLESHEET_FILE = 'styles.css';
import { REPORTS_STATE_IN_MEMORY_PROVIDERS } from './configs/reports-state';
import { SESSION_PROVIDERS } from './configs/session-providers';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DesignSystemComponent } from './pages/design-system/design-system.component';
import { StatementComponent } from './pages/statement/statement.component';
import { ReportsComponent } from './pages/reports/reports.component';

/**
 * Providers for the custom element bootstrap. Session providers come from
 * `SESSION_PROVIDERS` — the same ones `app.config.ts` uses — so the two bootstraps
 * never diverge silently.
 */
export const ELEMENT_MODULE_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  provideRouter(routes),
  provideAnimations(),
  provideNgxMask(),
  ...SESSION_PROVIDERS,
  // The URL here belongs to the host portal, not to us — see
  // `configs/reports-state.ts`. It is the only deliberate divergence between this
  // bootstrap and the SPA's, which is why it stays out of `SESSION_PROVIDERS`, a
  // list that exists precisely to keep the two from drifting apart by accident.
  ...REPORTS_STATE_IN_MEMORY_PROVIDERS,
  importProvidersFrom(CdsInputsModule, UiDirectivesModule),
];

/**
 * Module for the Web Component application.
 */
@NgModule({
  imports: [BrowserModule, CommonModule],
  providers: ELEMENT_MODULE_PROVIDERS,
})
export class ElementModule implements DoBootstrap {
  /**
   * Components exposed as custom elements for the Getnet portal to embed.
   *
   * Names follow `getnet-webcomponent-<function>`, and the function uses the same
   * vocabulary as `serasa-cl-webcomponents` — `statement` for the statement screen
   * and `reports` for the reports one, as over there. The portal integrates by tag
   * name, and diverging from the fleet's vocabulary would force whoever integrates
   * to learn a dictionary per brand.
   *
   * Each entry is a **whole page**, without the shell (`ShellComponent`): side menu
   * and title belong to the host portal, not to us.
   *
   * ## The `token` attribute
   *
   * Every screen except the design system extends `TokenExchange`, so each tag
   * accepts a `token` attribute:
   *
   * ```html
   * <getnet-webcomponent-statement token="<token do portal>"></getnet-webcomponent-statement>
   * ```
   *
   * That is the whole integration contract, and it is the same one serasa exposes.
   * The portal owns the login and hands over its own token; the component trades it
   * for a session through `POST /v2/auth/token_exchange`. It can be written after
   * the element is in the DOM — the exchange runs on `ngOnChanges`, not on init.
   *
   * `getnet-webcomponent-design-system` takes no token on purpose: it is a static
   * showcase, calls no API, and asking for a session to look at buttons would only
   * get in the way.
   */
  protected components = [
    {
      name: 'getnet-webcomponent-design-system',
      component: DesignSystemComponent,
    },
    {
      name: 'getnet-webcomponent-dashboard-finance',
      component: DashboardComponent,
    },
    {
      name: 'getnet-webcomponent-statement',
      component: StatementComponent,
    },
    /**
     * Does not touch the host's URL: `REPORTS_STATE_IN_MEMORY_PROVIDERS` above
     * turns off persisting the report state in query params. The why and the
     * measurement are in `configs/reports-state.ts`.
     */
    {
      name: 'getnet-webcomponent-reports',
      component: ReportsComponent,
    },
  ];

  /**
   * Injector for the application.
   */
  private readonly injector = inject(Injector);

  /**
   * Token storage, switched to `sessionStorage` on bootstrap.
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Registers every component as a custom element and prepares the host page.
   */
  public ngDoBootstrap(): void {
    this.components.forEach(({ name, component }) => {
      if (!customElements.get(name)) {
        customElements.define(name, createCustomElement(component, { injector: this.injector }));
      }
    });

    this.useSessionStorage();
    this.injectGlobalStyles();
    this.publishApiOrigin();
  }

  /**
   * Records which api-client this bundle talks to, on the document root.
   *
   * Only the validation host page reads it, and it exists because that page cannot know
   * the answer any other way. It used to guess from `location.hostname`: localhost meant
   * the local mock, anything else meant the published one. That holds for the two normal
   * cases and breaks on a third, which is the one someone actually hits when checking a
   * release: serving the **develop** artifact from a local static server. The page then
   * asked `localhost:3000` for a token while the bundle was pointed at the published
   * api-client, and reported "sem sessão" for a bundle that was perfectly healthy.
   *
   * A `data-` attribute rather than a global on `window`: it is inert, namespaced, and
   * cannot collide with anything the host portal has. The value is not a secret either —
   * it is already in the bundle and in every request the components make.
   */
  private publishApiOrigin(): void {
    document.documentElement.dataset['getnetApi'] = environment.apiUrl;
  }

  /**
   * Keeps the session in `sessionStorage` instead of ui-oauth's default `localStorage`.
   *
   * Only applies to this bootstrap, and that asymmetry with the SPA is the point. The
   * bundle runs inside the host portal, and `localStorage` is shared across every tab
   * of that origin and survives the browser closing: a session obtained through the
   * portal would outlive the portal's own session and leak into the next tab someone
   * opened. `sessionStorage` dies with the tab, which is the lifetime the portal's
   * session actually has. Same call `serasa-cl-webcomponents` makes.
   */
  private useSessionStorage(): void {
    this.tokens.setStorage(sessionStorage);
  }

  /**
   * Adds the bundle's stylesheet to the host page's `head`.
   *
   * Whoever consumes the bundle loads a single `main.js`. They have no way of knowing
   * a `styles.css` sits next to it, so without this the components mount completely
   * unstyled — every token, every icon font, the whole design system missing. It is
   * how serasa solves the same problem.
   *
   * The icon font is not a worry: the design system points at
   * `https://static.celero.mobi/fonts/icons/cds-icons.woff`, an absolute URL that
   * resolves from any origin. The Santander faces are relative (`url('…woff2')`) and
   * resolve against the stylesheet, so they follow it wherever it is served from.
   *
   * Two guards, both of which matter:
   *
   * - no `cdnUrl` means we do not know where the file is. Injecting `/styles.css` would
   *   ask the **portal's** origin for it and 404 there;
   * - the same file already linked means the page carries it — the case of our own host
   *   page, where Angular writes the `<link>` into the index at build time. Injecting
   *   again would fetch 374 KB twice.
   *
   * The second guard compares **resolved** URLs, not the attribute. Matching any
   * `href` ending in `styles.css` was the first attempt and it is wrong in the direction
   * that hurts: a portal with a `styles.css` of its own — hardly exotic — would satisfy
   * the guard, we would stand down, and every component would mount unstyled. Comparing
   * `link.href` (always absolute in the DOM) against the resolved target means only our
   * own file counts, while the host page's relative `styles.css` still resolves to the
   * same absolute URL and is correctly recognised.
   */
  private injectGlobalStyles(): void {
    if (!environment.cdnUrl) {
      return;
    }

    const href = new URL(`${environment.cdnUrl}/${STYLESHEET_FILE}`, document.baseURI).href;
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));

    if (links.some((link) => link.href === href)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}
